import type { ProjectModelDraft } from '../../project/model';
import {
  insetOverlayScene,
  insetPresentationScale,
  deriveInsetView,
  projectInsetFootprint,
  type GroundFootprint,
} from '../inset';
import { compositionLogicalViewport } from '../scale';
import { withRenderedStaticMapFrame, type StaticMapRendererDependencies } from './mapRenderer';
import { StaticExportError } from './model';
import type { OutputPoint, StaticCompositionRenderPlan } from './planning';

export interface InsetCapture {
  readonly canvas: HTMLCanvasElement;
  /** Normalized inset screen points; identical geometry in raster and PDF. */
  readonly points: readonly OutputPoint[];
}

export async function withRenderedInset<T>(
  plan: StaticCompositionRenderPlan,
  project: ProjectModelDraft,
  footprint: GroundFootprint | null,
  consume: (capture: InsetCapture) => T | Promise<T>,
  dependencies: StaticMapRendererDependencies = {},
): Promise<T> {
  const inset = plan.insetMap;
  if (!inset || !footprint)
    throw new StaticExportError(
      'map-initialisation',
      'The main map ground footprint is unavailable for the inset.',
    );
  const logical = compositionLogicalViewport(
    inset.boundsMm.width,
    inset.boundsMm.height,
    plan.mapFrame.logicalPixelsPerMm,
  );
  const view = deriveInsetView(
    footprint,
    logical.widthPx,
    logical.heightPx,
    inset.config.contextMargin,
  );
  if (!view)
    throw new StaticExportError(
      'map-initialisation',
      'The inset context extent cannot be rendered safely.',
    );
  const scene = insetOverlayScene(project, inset.config);
  const scale = insetPresentationScale(dependencies.mainZoom ?? plan.mapFrame.view.zoom, view.zoom);
  const pixelRatio = plan.mapFrame.pixelRatio;
  const insetPlan = {
    ...plan,
    mapFrame: {
      ...plan.mapFrame,
      boundsMm: inset.boundsMm,
      boundsPx: inset.boundsPx,
      logicalViewport: logical,
      backingSize: {
        widthPx: Math.floor(logical.widthPx * pixelRatio),
        heightPx: Math.floor(logical.heightPx * pixelRatio),
      },
      view,
      borderEnabled: false,
    },
  };
  const points = projectInsetFootprint(footprint, view, logical.widthPx, logical.heightPx).map(
    (point) => ({ x: point.x / logical.widthPx, y: point.y / logical.heightPx }),
  );
  return withRenderedStaticMapFrame(
    insetPlan,
    project.map.backgroundColour,
    project.sources,
    scene.layers,
    (canvas) => consume({ canvas, points }),
    {
      ...dependencies,
      symbolAssets: project.symbolAssets,
      sceneMode: 'complete',
      presentationScale: scale,
      renderWorldCopies: true,
      basemapConfiguration: project.map.basemap,
      annotationLayers: scene.annotationLayers ?? [],
      activeFocusAreaId: project.activeFocusAreaId,
    },
  );
}
